import type { ScaleName } from './theory';

/**
 * Data for the generative score. Every theme is an original piece described
 * by tempo, key/mode, chord progressions (Roman numerals), and a list of parts
 * (instrument + role + register). The sequencer turns a theme into bars of
 * notes; the music engine plays them.
 *
 * Shared identity: melodic parts can open a section with the Aetherfall
 * leitmotif (root, up a fifth, then one step higher, held) so every region
 * sounds like the same world in a different costume.
 */

export type ThemeId =
  | 'title'
  | 'meadow'
  | 'town'
  | 'desert'
  | 'frost'
  | 'dungeon'
  | 'rift'
  | 'boss'
  | 'raid'
  | 'sea'
  | 'arena'
  | 'victory';
export const THEME_IDS: readonly ThemeId[] = [
  'title',
  'meadow',
  'town',
  'desert',
  'frost',
  'dungeon',
  'rift',
  'boss',
  'raid',
  'sea',
  'arena',
  'victory',
];

export type AmbienceId =
  'none' | 'meadow' | 'town' | 'desert' | 'frost' | 'dungeon' | 'rift' | 'sea';

export type InstrumentId =
  | 'pad'
  | 'strings'
  | 'choir'
  | 'choirOo'
  | 'harp'
  | 'oud'
  | 'keys'
  | 'bell'
  | 'celesta'
  | 'tubular'
  | 'flute'
  | 'brass'
  | 'horn'
  | 'bass'
  | 'ostinato'
  | 'drone'
  | 'taiko'
  | 'timpani'
  | 'snare'
  | 'shaker'
  | 'frame'
  | 'tek'
  | 'swell'
  | 'gong';

/** `combat` parts only sound while combat intensity is on. */
export type Layer = 'base' | 'combat';

export type PartRole =
  | 'chord'
  | 'arp'
  | 'bass'
  | 'melody'
  | 'ostinato'
  | 'drums'
  | 'stab'
  | 'sparkle'
  | 'drone'
  | 'swell';

export interface PartDef {
  inst: InstrumentId;
  role: PartRole;
  layer?: Layer;
  /** MIDI register [lo, hi] (tuned drums use it for their pitch). */
  range?: [number, number];
  /** Base velocity 0..1. */
  vel?: number;
  /** Active sections of the 4-section cycle (default all). */
  sections?: number[];
  /** Bar indices inside a section where the part plays (default all). */
  bars?: number[];
  /** Step length in beats for arp / sparkle. */
  rate?: number;
  arp?: 'up' | 'down' | 'updown' | 'broken' | 'random';
  /**
   * One bar of steps. Drums/stabs: `X` accent, `x` normal, `o` ghost, `r` roll,
   * `.` rest. Ostinato: `r` root, `3` third, `5` fifth, `8` octave, `.` rest.
   */
  pattern?: string;
  bass?: 'whole' | 'half' | 'waltz' | 'pulse8' | 'dotted' | 'gallop' | 'pedal';
  /** Per-step probability (sparkle). */
  prob?: number;
  /** Melody: chance to open a section with the leitmotif. */
  motif?: number;
  /** Melody: grace-note ornament probability. */
  ornament?: number;
  /** Melody: 0..1, lower = more resting bars. */
  density?: number;
  /** Note-length multiplier (staccato < 1 < legato). */
  legato?: number;
  /** Drums: chance of a fill at the end of each 4-bar phrase. */
  fill?: number;
  /** Chords: add the root an octave below. */
  double?: boolean;
}

export interface ThemeDef {
  id: ThemeId;
  name: string;
  bpm: number;
  /** Quarter-note beats per bar (6/8 = 3 with `feel: '68'`). */
  beatsPerBar: number;
  feel?: '44' | '34' | '68';
  /** Tonic as a MIDI note; its pitch class is the key. */
  tonic: number;
  scale: ScaleName;
  /** Section k of the piece uses progressions[k % n]. */
  progressions: string[][];
  barsPerChord: number;
  sectionBars?: number;
  /** Velocity/probability multiplier for each section of the 4-section cycle. */
  intensity?: [number, number, number, number];
  parts: PartDef[];
  /** Melody rhythm cells (durations in beats, one bar each). */
  rhythms?: number[][];
  /** Reverb send 0..1. */
  reverb?: number;
  /** Output gain of the whole arrangement (calibrated per theme for even loudness). */
  level?: number;
  ambience?: AmbienceId;
  /** Lowpass cutoff of the pad section (Hz): the theme's warmth. */
  padCutoff?: number;
}

/** The Aetherfall leitmotif: scale steps above the chord root and rhythms per metre. */
export const LEITMOTIF = {
  steps: [0, 4, 5],
  rhythm: { '44': [1, 0.5, 2.5], '34': [1, 0.5, 1.5], '68': [1, 0.5, 1.5] },
} as const;

export const DEFAULT_RHYTHMS: Record<'44' | '34' | '68', number[][]> = {
  '44': [
    [1, 1, 2],
    [1.5, 0.5, 2],
    [0.5, 0.5, 1, 2],
    [2, 1, 1],
    [1, 0.5, 0.5, 2],
    [1, 1, 1, 1],
    [3, 1],
    [1.5, 0.5, 1, 1],
    [4],
  ],
  '34': [[1, 1, 1], [2, 1], [1.5, 0.5, 1], [1, 2], [0.5, 0.5, 2], [3]],
  '68': [[1.5, 1.5], [1, 0.5, 1.5], [1.5, 1, 0.5], [0.5, 0.5, 0.5, 1.5], [1, 0.5, 1, 0.5], [3]],
};

const ALL_COMBAT_16: PartDef[] = [
  {
    inst: 'taiko',
    role: 'drums',
    layer: 'combat',
    pattern: 'X.....x.X...x...',
    vel: 0.75,
    fill: 0.45,
  },
  { inst: 'shaker', role: 'drums', layer: 'combat', pattern: 'x.x.x.x.x.x.x.x.', vel: 0.35 },
];

export const THEMES: Record<ThemeId, ThemeDef> = {
  title: {
    id: 'title',
    name: 'Aetherfall',
    bpm: 72,
    beatsPerBar: 4,
    tonic: 50,
    scale: 'major',
    progressions: [
      ['I', 'V', 'vi', 'IV'],
      ['vi', 'IV', 'I', 'V'],
      ['IV', 'V', 'iii', 'vi'],
      ['I', 'bVII', 'IV', 'I'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.75, 0.95, 1, 0.85],
    reverb: 0.55,
    level: 1.21,
    ambience: 'sea',
    padCutoff: 1400,
    parts: [
      { inst: 'strings', role: 'chord', range: [50, 74], vel: 0.45, double: true },
      { inst: 'choir', role: 'chord', range: [57, 76], vel: 0.55, sections: [1, 2] },
      {
        inst: 'horn',
        role: 'melody',
        range: [62, 79],
        vel: 0.75,
        motif: 0.9,
        density: 0.85,
        sections: [1, 2, 3],
      },
      {
        inst: 'harp',
        role: 'arp',
        range: [62, 88],
        rate: 0.5,
        arp: 'updown',
        vel: 0.4,
        sections: [0, 1, 3],
      },
      { inst: 'bass', role: 'bass', range: [38, 52], bass: 'half', vel: 0.55 },
      {
        inst: 'timpani',
        role: 'drums',
        range: [38, 50],
        pattern: 'X...............',
        vel: 0.65,
        fill: 0.5,
        sections: [1, 2],
      },
      { inst: 'swell', role: 'swell', vel: 0.45, sections: [0, 1, 2] },
      ...ALL_COMBAT_16,
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [50, 62],
        pattern: 'r.r.5.r.8.5.r.5.',
        vel: 0.5,
      },
    ],
  },

  meadow: {
    id: 'meadow',
    name: 'Emberwild Morning',
    bpm: 88,
    beatsPerBar: 4,
    tonic: 55,
    scale: 'major',
    progressions: [
      ['I', 'IV', 'vi', 'V'],
      ['vi', 'IV', 'I', 'V'],
      ['IV', 'I', 'ii', 'V'],
      ['I', 'iii', 'IV', 'V'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.8, 1, 0.95, 0.75],
    reverb: 0.38,
    level: 0.81,
    ambience: 'meadow',
    padCutoff: 1100,
    parts: [
      { inst: 'pad', role: 'chord', range: [50, 69], vel: 0.5 },
      { inst: 'harp', role: 'arp', range: [55, 81], rate: 0.5, arp: 'broken', vel: 0.42 },
      {
        inst: 'flute',
        role: 'melody',
        range: [67, 88],
        vel: 0.55,
        motif: 0.4,
        density: 0.7,
        sections: [0, 1, 2],
      },
      { inst: 'bass', role: 'bass', range: [36, 50], bass: 'half', vel: 0.45 },
      {
        inst: 'celesta',
        role: 'sparkle',
        range: [79, 96],
        rate: 0.5,
        prob: 0.06,
        vel: 0.35,
        sections: [2, 3],
      },
      { inst: 'strings', role: 'chord', range: [55, 76], vel: 0.45, sections: [1, 2] },
      {
        inst: 'taiko',
        role: 'drums',
        layer: 'combat',
        pattern: 'X...x...X..xx...',
        vel: 0.7,
        fill: 0.4,
      },
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [43, 57],
        pattern: 'r.5.r.8.r.5.r.8.',
        vel: 0.5,
      },
      { inst: 'shaker', role: 'drums', layer: 'combat', pattern: 'x.x.x.x.x.x.x.x.', vel: 0.35 },
    ],
  },

  town: {
    id: 'town',
    name: 'Havenreach Waltz',
    bpm: 100,
    beatsPerBar: 3,
    feel: '34',
    tonic: 53,
    scale: 'major',
    progressions: [
      ['I', 'vi', 'ii', 'V'],
      ['IV', 'I', 'ii', 'V'],
      ['I', 'iii', 'IV', 'V'],
      ['vi', 'ii', 'V', 'I'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.85, 1, 1, 0.8],
    reverb: 0.33,
    level: 0.9,
    ambience: 'town',
    padCutoff: 1200,
    rhythms: DEFAULT_RHYTHMS['34'],
    parts: [
      { inst: 'bass', role: 'bass', range: [36, 50], bass: 'waltz', vel: 0.55 },
      { inst: 'keys', role: 'stab', range: [57, 72], pattern: '....x...x...', vel: 0.42 },
      { inst: 'strings', role: 'chord', range: [53, 74], vel: 0.45, sections: [1, 2, 3] },
      {
        inst: 'flute',
        role: 'melody',
        range: [69, 89],
        vel: 0.55,
        motif: 0.35,
        density: 0.8,
        sections: [0, 1, 2],
      },
      {
        inst: 'celesta',
        role: 'sparkle',
        range: [81, 96],
        rate: 0.5,
        prob: 0.07,
        vel: 0.3,
        sections: [2],
      },
      { inst: 'harp', role: 'arp', range: [60, 84], rate: 0.5, arp: 'up', vel: 0.3, sections: [3] },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: 'x.....x.x...', vel: 0.4 },
      { inst: 'taiko', role: 'drums', layer: 'combat', pattern: 'X.....x.....', vel: 0.6 },
    ],
  },

  desert: {
    id: 'desert',
    name: 'Sunscar Winds',
    bpm: 84,
    beatsPerBar: 4,
    tonic: 52,
    scale: 'phrygianDominant',
    progressions: [
      ['I', 'bII', 'I', 'bvii'],
      ['iv', 'bII', 'I', 'I'],
      ['I', 'bII', 'iv', 'I'],
      ['bvii', 'bII', 'I', 'I'],
    ],
    barsPerChord: 2,
    sectionBars: 8,
    intensity: [0.8, 1, 1, 0.8],
    reverb: 0.4,
    level: 0.99,
    ambience: 'desert',
    padCutoff: 900,
    parts: [
      { inst: 'drone', role: 'drone', range: [40, 52], vel: 0.55 },
      {
        inst: 'oud',
        role: 'melody',
        range: [59, 79],
        vel: 0.62,
        motif: 0.3,
        density: 0.75,
        ornament: 0.3,
      },
      {
        inst: 'frame',
        role: 'drums',
        pattern: 'X..x..X...x.X...',
        vel: 0.6,
        fill: 0.3,
        sections: [1, 2, 3],
      },
      { inst: 'tek', role: 'drums', pattern: '..x.x...x.x..xx.', vel: 0.45, sections: [1, 2, 3] },
      { inst: 'pad', role: 'chord', range: [52, 67], vel: 0.35 },
      { inst: 'choir', role: 'chord', range: [64, 79], vel: 0.35, sections: [2] },
      {
        inst: 'oud',
        role: 'arp',
        range: [52, 71],
        rate: 0.5,
        arp: 'random',
        vel: 0.28,
        sections: [2, 3],
      },
      {
        inst: 'taiko',
        role: 'drums',
        layer: 'combat',
        pattern: 'X..X..X.X..X..X.',
        vel: 0.75,
        fill: 0.4,
      },
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [40, 55],
        pattern: 'r.8.r.8.r.8.r58.',
        vel: 0.45,
      },
      { inst: 'shaker', role: 'drums', layer: 'combat', pattern: 'x.xxx.xxx.xxx.xx', vel: 0.3 },
    ],
  },

  frost: {
    id: 'frost',
    name: 'Frosthollow Vigil',
    bpm: 64,
    beatsPerBar: 4,
    tonic: 50,
    scale: 'minor',
    progressions: [
      ['i', 'bVI', 'bIII', 'bVII'],
      ['iv', 'bVI', 'i', 'v'],
      ['i', 'bVII', 'bVI', 'bVII'],
      ['bVI', 'iv', 'i', 'i'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.75, 0.95, 1, 0.8],
    reverb: 0.58,
    level: 1.17,
    ambience: 'frost',
    padCutoff: 1000,
    parts: [
      { inst: 'choirOo', role: 'chord', range: [50, 72], vel: 0.5 },
      { inst: 'strings', role: 'chord', range: [45, 69], vel: 0.45, sections: [1, 2, 3] },
      { inst: 'celesta', role: 'sparkle', range: [74, 98], rate: 0.5, prob: 0.12, vel: 0.3 },
      {
        inst: 'celesta',
        role: 'melody',
        range: [69, 88],
        vel: 0.75,
        motif: 0.35,
        density: 0.5,
        legato: 1.4,
        sections: [1, 2],
      },
      { inst: 'bass', role: 'bass', range: [38, 50], bass: 'whole', vel: 0.4 },
      {
        inst: 'tubular',
        role: 'drums',
        range: [62, 74],
        pattern: 'X...............',
        vel: 0.35,
        sections: [0, 2],
        bars: [0, 4],
      },
      {
        inst: 'timpani',
        role: 'drums',
        range: [38, 50],
        pattern: 'x...............',
        vel: 0.7,
        sections: [1, 2],
        fill: 0.3,
      },
      ...ALL_COMBAT_16,
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [45, 57],
        pattern: 'r.r.5.r.r.r.8.5.',
        vel: 0.5,
      },
    ],
  },

  dungeon: {
    id: 'dungeon',
    name: 'Hollow Depths',
    bpm: 58,
    beatsPerBar: 4,
    tonic: 48,
    scale: 'phrygian',
    progressions: [
      ['i', 'bII', 'i', 'bvii'],
      ['i', 'bVI', 'bII', 'i'],
      ['iv', 'bII', 'i', 'i'],
      ['i', 'i', 'bII', 'bII'],
    ],
    barsPerChord: 2,
    sectionBars: 8,
    intensity: [0.7, 0.9, 1, 0.8],
    reverb: 0.62,
    level: 1.21,
    ambience: 'dungeon',
    padCutoff: 700,
    parts: [
      { inst: 'drone', role: 'drone', range: [36, 48], vel: 0.6 },
      { inst: 'pad', role: 'chord', range: [48, 64], vel: 0.4 },
      { inst: 'choirOo', role: 'chord', range: [55, 72], vel: 0.35, sections: [1, 2] },
      {
        inst: 'harp',
        role: 'melody',
        range: [55, 74],
        vel: 0.6,
        density: 0.4,
        motif: 0.25,
        sections: [1, 2, 3],
      },
      { inst: 'taiko', role: 'drums', pattern: 'X...............', vel: 0.45, sections: [2, 3] },
      {
        inst: 'tubular',
        role: 'drums',
        range: [48, 60],
        pattern: '........x.......',
        vel: 0.4,
        sections: [1, 3],
        bars: [1, 5],
      },
      {
        inst: 'taiko',
        role: 'drums',
        layer: 'combat',
        pattern: 'X..x....X..x.x..',
        vel: 0.75,
        fill: 0.5,
      },
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [36, 50],
        pattern: 'r.r.r.r.5.5.r.r.',
        vel: 0.5,
      },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: '....x.......x..o', vel: 0.35 },
    ],
  },

  rift: {
    id: 'rift',
    name: 'Rift Pulse',
    bpm: 112,
    beatsPerBar: 4,
    tonic: 47,
    scale: 'phrygian',
    progressions: [
      ['i', 'bII', 'i', 'bII'],
      ['bVI', 'bvii', 'i', 'i'],
      ['i', 'bII', 'bvii', 'i'],
      ['iv', 'bII', 'i', 'bII'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.8, 1, 1, 0.9],
    reverb: 0.45,
    level: 1.21,
    ambience: 'rift',
    padCutoff: 1000,
    parts: [
      { inst: 'bass', role: 'bass', range: [35, 47], bass: 'pulse8', vel: 0.5 },
      { inst: 'pad', role: 'chord', range: [47, 66], vel: 0.4 },
      {
        inst: 'harp',
        role: 'arp',
        range: [59, 83],
        rate: 0.25,
        arp: 'random',
        vel: 0.25,
        sections: [1, 2, 3],
      },
      { inst: 'choir', role: 'chord', range: [59, 76], vel: 0.35, sections: [2, 3] },
      {
        inst: 'taiko',
        role: 'drums',
        pattern: 'X..x..X...x.X...',
        vel: 0.7,
        sections: [1, 2, 3],
        fill: 0.4,
      },
      { inst: 'shaker', role: 'drums', pattern: 'x.x.x.x.x.x.x.x.', vel: 0.35, sections: [2, 3] },
      { inst: 'swell', role: 'swell', vel: 0.45 },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: 'x...X..x..x.X...', vel: 0.5 },
      {
        inst: 'brass',
        role: 'stab',
        layer: 'combat',
        range: [47, 64],
        pattern: 'X.......x.......',
        vel: 0.5,
      },
      {
        inst: 'ostinato',
        role: 'ostinato',
        layer: 'combat',
        range: [47, 59],
        pattern: 'r8r8r8r8r8r85858',
        vel: 0.42,
      },
    ],
  },

  boss: {
    id: 'boss',
    name: 'Colossus',
    bpm: 132,
    beatsPerBar: 4,
    tonic: 52,
    scale: 'harmonicMinor',
    progressions: [
      ['i', 'bVI', 'bVII', 'i'],
      ['i', 'iv', 'bVI', 'V'],
      ['bVI', 'bVII', 'i', 'i'],
      ['iv', 'V', 'i', 'bII'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.85, 1, 1, 0.9],
    reverb: 0.35,
    level: 0.74,
    padCutoff: 1500,
    parts: [
      {
        inst: 'ostinato',
        role: 'ostinato',
        range: [52, 64],
        pattern: 'r8r5r8r5r8r5r858',
        vel: 0.5,
      },
      { inst: 'strings', role: 'chord', range: [52, 76], vel: 0.45 },
      {
        inst: 'brass',
        role: 'stab',
        range: [52, 69],
        pattern: 'X.....x...X.....',
        vel: 0.55,
        sections: [1, 2, 3],
      },
      {
        inst: 'horn',
        role: 'melody',
        range: [59, 79],
        vel: 0.65,
        motif: 0.6,
        density: 0.8,
        sections: [1, 2],
      },
      { inst: 'choir', role: 'chord', range: [57, 76], vel: 0.5, sections: [2, 3] },
      { inst: 'taiko', role: 'drums', pattern: 'X..x..X.X..x..X.', vel: 0.55, fill: 0.5 },
      { inst: 'timpani', role: 'drums', range: [40, 52], pattern: 'X.......x.......', vel: 0.6 },
      { inst: 'bass', role: 'bass', range: [33, 47], bass: 'pulse8', vel: 0.5 },
      { inst: 'swell', role: 'swell', vel: 0.5 },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: 'o.o.X.o.o.o.X.oo', vel: 0.5 },
      { inst: 'shaker', role: 'drums', layer: 'combat', pattern: 'x.x.x.x.x.x.x.x.', vel: 0.3 },
      { inst: 'taiko', role: 'drums', layer: 'combat', pattern: '..x...x...x...x.', vel: 0.6 },
    ],
  },

  raid: {
    id: 'raid',
    name: 'Siege of the Hollow Throne',
    bpm: 140,
    beatsPerBar: 4,
    tonic: 50,
    scale: 'harmonicMinor',
    progressions: [
      ['i', 'bII', 'bVI', 'V'],
      ['i', 'bVI', 'iv', 'V'],
      ['bVI', 'bII', 'i', 'V'],
      ['iv', 'bVI', 'bII', 'V'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.9, 1, 1, 0.95],
    reverb: 0.4,
    level: 0.71,
    padCutoff: 1600,
    parts: [
      {
        inst: 'ostinato',
        role: 'ostinato',
        range: [50, 62],
        pattern: 'r8r5r8r5r8r5r858',
        vel: 0.5,
      },
      { inst: 'strings', role: 'chord', range: [50, 74], vel: 0.5 },
      { inst: 'choir', role: 'chord', range: [57, 77], vel: 0.55, sections: [1, 2, 3] },
      {
        inst: 'brass',
        role: 'stab',
        range: [50, 69],
        pattern: 'X..X......X.X...',
        vel: 0.4,
        sections: [1, 2, 3],
      },
      {
        inst: 'horn',
        role: 'melody',
        range: [57, 79],
        vel: 0.68,
        motif: 0.7,
        density: 0.85,
        sections: [1, 2],
      },
      {
        inst: 'tubular',
        role: 'drums',
        range: [62, 74],
        pattern: 'X.......X.......',
        vel: 0.32,
        sections: [2],
      },
      { inst: 'taiko', role: 'drums', pattern: 'X.xX..X.X.xX..X.', vel: 0.45, fill: 0.6 },
      { inst: 'timpani', role: 'drums', range: [38, 50], pattern: 'X...X...X...X...', vel: 0.34 },
      { inst: 'bass', role: 'bass', range: [33, 47], bass: 'pulse8', vel: 0.55 },
      { inst: 'gong', role: 'drums', pattern: 'X...............', vel: 0.5, bars: [0] },
      { inst: 'swell', role: 'swell', vel: 0.5 },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: 'X.x.X.xxX.x.X.xx', vel: 0.36 },
      { inst: 'shaker', role: 'drums', layer: 'combat', pattern: 'x.x.x.x.x.x.x.x.', vel: 0.3 },
    ],
  },

  sea: {
    id: 'sea',
    name: 'The Gleaming Sea',
    bpm: 120,
    beatsPerBar: 3,
    feel: '68',
    tonic: 57,
    scale: 'major',
    progressions: [
      ['I', 'IV', 'I', 'V'],
      ['vi', 'IV', 'I', 'V'],
      ['IV', 'V', 'vi', 'IV'],
      ['I', 'V', 'IV', 'I'],
    ],
    barsPerChord: 2,
    sectionBars: 8,
    intensity: [0.8, 1, 1, 0.85],
    reverb: 0.45,
    level: 0.78,
    ambience: 'sea',
    padCutoff: 1300,
    rhythms: DEFAULT_RHYTHMS['68'],
    parts: [
      { inst: 'strings', role: 'chord', range: [52, 74], vel: 0.5 },
      { inst: 'harp', role: 'arp', range: [57, 81], rate: 0.5, arp: 'up', vel: 0.4 },
      {
        inst: 'horn',
        role: 'melody',
        range: [64, 83],
        vel: 0.68,
        motif: 0.7,
        density: 0.85,
        sections: [1, 2, 3],
      },
      { inst: 'bass', role: 'bass', range: [40, 55], bass: 'dotted', vel: 0.5 },
      {
        inst: 'timpani',
        role: 'drums',
        range: [40, 52],
        pattern: 'X.....x.....',
        vel: 0.5,
        sections: [1, 2],
      },
      { inst: 'shaker', role: 'drums', pattern: 'x.x.x.x.x.x.', vel: 0.3, sections: [2, 3] },
      { inst: 'choir', role: 'chord', range: [57, 76], vel: 0.4, sections: [2] },
      {
        inst: 'taiko',
        role: 'drums',
        layer: 'combat',
        pattern: 'X.....X.x...',
        vel: 0.7,
        fill: 0.4,
      },
      { inst: 'snare', role: 'drums', layer: 'combat', pattern: 'x.x.X.x.x.X.', vel: 0.35 },
    ],
  },

  arena: {
    id: 'arena',
    name: 'The Crucible',
    bpm: 124,
    beatsPerBar: 4,
    tonic: 45,
    scale: 'minor',
    progressions: [
      ['i', 'bVII', 'bVI', 'bVII'],
      ['i', 'iv', 'bVI', 'V'],
      ['bVI', 'bVII', 'i', 'i'],
      ['iv', 'i', 'bVII', 'V'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [0.9, 1, 1, 0.9],
    reverb: 0.3,
    level: 0.78,
    padCutoff: 1500,
    parts: [
      { inst: 'bass', role: 'bass', range: [33, 47], bass: 'pulse8', vel: 0.55 },
      {
        inst: 'ostinato',
        role: 'ostinato',
        range: [45, 57],
        pattern: 'r.r.8.r.r.r.8.5.',
        vel: 0.6,
      },
      { inst: 'brass', role: 'stab', range: [52, 69], pattern: 'X..X......X.....', vel: 0.55 },
      { inst: 'strings', role: 'chord', range: [52, 74], vel: 0.45, sections: [1, 2, 3] },
      { inst: 'taiko', role: 'drums', pattern: 'X...X.x.X...X.x.', vel: 0.55, fill: 0.5 },
      { inst: 'snare', role: 'drums', pattern: '....X.......X...', vel: 0.55 },
      {
        inst: 'shaker',
        role: 'drums',
        pattern: 'x.x.x.x.x.x.x.x.',
        vel: 0.28,
        sections: [1, 2, 3],
      },
      {
        inst: 'horn',
        role: 'melody',
        range: [57, 76],
        vel: 0.62,
        motif: 0.3,
        density: 0.7,
        sections: [1, 2],
      },
      { inst: 'choir', role: 'chord', range: [57, 76], vel: 0.45, sections: [2] },
      {
        inst: 'snare',
        role: 'drums',
        layer: 'combat',
        pattern: '..o...o.o.o...oo',
        vel: 0.4,
        fill: 0.5,
      },
      { inst: 'taiko', role: 'drums', layer: 'combat', pattern: '..x.......x..x..', vel: 0.6 },
    ],
  },

  victory: {
    id: 'victory',
    name: 'Lantern Rekindled',
    bpm: 96,
    beatsPerBar: 4,
    tonic: 46,
    scale: 'major',
    progressions: [
      ['I', 'IV', 'V', 'I'],
      ['vi', 'IV', 'I', 'V'],
      ['IV', 'I', 'ii', 'V'],
      ['I', 'iii', 'IV', 'I'],
    ],
    barsPerChord: 1,
    sectionBars: 8,
    intensity: [1, 0.9, 0.85, 0.8],
    reverb: 0.45,
    level: 0.77,
    padCutoff: 1500,
    parts: [
      { inst: 'brass', role: 'melody', range: [58, 77], vel: 0.7, motif: 1, density: 0.9 },
      { inst: 'strings', role: 'chord', range: [53, 74], vel: 0.35, double: true },
      { inst: 'choir', role: 'chord', range: [58, 77], vel: 0.5, sections: [0, 1] },
      {
        inst: 'timpani',
        role: 'drums',
        range: [34, 46],
        pattern: 'X...x...X...x...',
        vel: 0.44,
        sections: [0],
        fill: 0.6,
      },
      { inst: 'harp', role: 'arp', range: [58, 84], rate: 0.5, arp: 'updown', vel: 0.35 },
      { inst: 'bass', role: 'bass', range: [34, 50], bass: 'half', vel: 0.5 },
      {
        inst: 'brass',
        role: 'stab',
        range: [53, 70],
        pattern: 'X...............',
        vel: 0.45,
        sections: [0],
        bars: [0, 4],
      },
    ],
  },
};

/** Picks a theme for a zone id (or zone kind) from the content bible. */
export function themeForZone(zoneId: string, kind?: string): ThemeId {
  const id = zoneId.toLowerCase();
  if (id.startsWith('colossus')) return 'boss';
  const byId: Record<string, ThemeId> = {
    emberwild: 'meadow',
    havenreach: 'town',
    sunscar: 'desert',
    frosthollow: 'frost',
    crypt: 'dungeon',
    tomb: 'dungeon',
    keep: 'dungeon',
    rift: 'rift',
    deepvault: 'raid',
    siege: 'raid',
    crucible: 'arena',
    sea: 'sea',
    haven: 'meadow',
    glimmer: 'meadow',
    title: 'title',
  };
  const hit = byId[id];
  if (hit) return hit;
  const byKind: Record<string, ThemeId> = {
    field: 'meadow',
    town: 'town',
    dungeon: 'dungeon',
    rift: 'rift',
    colossus: 'boss',
    deepvault: 'raid',
    siege: 'raid',
    arena: 'arena',
    sea: 'sea',
    island: 'meadow',
    haven: 'meadow',
  };
  return (kind && byKind[kind]) || 'meadow';
}
